import { Card } from 'react-bootstrap';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import OrganizationDetails from 'components/global/OrganizationsList/components/OrganizationDetails';
import { fetchOrganization } from 'components/global/OrganizationsList/OrganizationsList.helpers';
import BreadCrumbWrapper from 'components/global/BreadCrumbs/BreadCrumbs';
import { organizationDetailsSignal } from 'components/global/OrganizationsList/OrganizationsList.signal';
import { checkEdoPermissionsAcrossPrograms } from '../../../../libs/edoPermissions';
import userAccountSignal from '../../../../signals/UserAccount.signal';
import api from '../../../../api/api';
import loaderSignal from '../../../../signals/Loader.signal';

const canEditAnyBusiness = (userData) => {
  if (!userData) return false;

  const accountRole = userData?.role;
  if (
    accountRole === 'SUPER_ADMIN' ||
    accountRole === 'ADMIN' ||
    accountRole === 'OWNER'
  ) {
    return true;
  }

  if (accountRole !== 'STAFF') {
    return false;
  }

  const accesses = userData?.edoUserProgramAccesses || [];
  return accesses.some((access) => access.role === 'ADMIN' || access.role === 'MANAGER');
};

const canAnalystEditLinkedBusiness = (userData, selectedOrganization) => {
  if (!userData || userData?.role !== 'STAFF') return false;

  const accesses = userData?.edoUserProgramAccesses || [];
  const analystProgramIds = accesses
    .filter((access) => access.role === 'ANALYST' || access.role === 'STAFF')
    .map((access) => access.fundingProgramId);

  if (analystProgramIds.length === 0) return false;

  const applications = selectedOrganization?.applications || [];
  return applications.some((application) => {
    const fundingProgramId =
      application?.fundingProgramMembership?.fundingProgramId || application?.fundingProgramId;
    return analystProgramIds.includes(fundingProgramId);
  });
};

const EdoBusinessDetail = () => {
  const { pathname } = useLocation();
  const history = useHistory();
  const segments = pathname.split('/');
  const organizationId = segments[segments.length - 1];
  const { selectedOrganization } = organizationDetailsSignal.value;
  const [canViewMaintenance, setCanViewMaintenance] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  let labels = [];

  if (pathname.startsWith('/edo/portfolio')) {
    const application = selectedOrganization?.applications?.find(app => app.id === Number(segments[3]));
    labels = [
      '',
      !!application ? `${application.fundingProgramMembership?.lender?.name} - ${selectedOrganization?.name}` : '',
      selectedOrganization?.name || '',
    ];
  } else {
    labels = [
      '',
      selectedOrganization?.name || '',
    ];
  }

  // Check if user has canViewMaintenance permission on any program
  useEffect(() => {
    let isMounted = true;
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        console.warn('Permission check timeout - allowing access');
        setIsChecking(false);
        setCanViewMaintenance(true); // Allow access on timeout to prevent infinite loading
        loaderSignal.reset();
      }
    }, 10000); // 10 second timeout

    const checkViewMaintenancePermission = async () => {
      try {
        setIsChecking(true);
        loaderSignal.update({ isContentLoading: true });
        const userData = userAccountSignal.value.userData;
        
        // Wait for userData to be loaded
        if (!userData || !userData.account) {
          console.warn('User data not available yet');
          if (isMounted) {
            setIsChecking(false);
            loaderSignal.reset();
          }
          return;
        }

        // Fetch funding programs for the EDO
        const fundingPrograms = await api.get({
          path: '/fundingPrograms',
          options: {
            where: {
              edoId: userData.account.edoId,
            },
          },
        });

        if (!isMounted) return;

        if (!fundingPrograms || fundingPrograms.length === 0) {
          console.warn('No funding programs found for EDO');
          setCanViewMaintenance(false);
          setIsChecking(false);
          loaderSignal.reset();
          clearTimeout(timeoutId);
          return;
        }

        // Check if user has canViewMaintenance permission on any program
        const permissions = await checkEdoPermissionsAcrossPrograms(
          fundingPrograms,
          ['canViewMaintenance']
        );

        if (!isMounted) return;

        setCanViewMaintenance(permissions.canViewMaintenance);
      } catch (error) {
        console.error('Error checking view maintenance permission:', error);
        if (isMounted) {
          setCanViewMaintenance(false);
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
          loaderSignal.reset();
          clearTimeout(timeoutId);
        }
      }
    };

    checkViewMaintenancePermission();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [userAccountSignal.value.userData]);

  const init = useCallback(async () => {
    if (canViewMaintenance) {
      await fetchOrganization(organizationId, '/v2/businesses');
    }
  }, [organizationId, canViewMaintenance]);

  useEffect(() => {
    init();
  }, [init]);

  if (isChecking) {
    return (
      <BreadCrumbWrapper labels={labels}>
        <Card>
          <Card.Body>
            <p className="text-center">Loading...</p>
          </Card.Body>
        </Card>
      </BreadCrumbWrapper>
    );
  }

  if (!canViewMaintenance) {
    return (
      <BreadCrumbWrapper labels={labels}>
        <Card>
          <Card.Body>
            <h3 className="text-center">Access Denied</h3>
            <p className="text-center">
              You do not have permission to view business details.
            </p>
          </Card.Body>
        </Card>
      </BreadCrumbWrapper>
    );
  }

  const userData = userAccountSignal.value.userData;
  const canEditBusiness =
    pathname.startsWith('/edo/business/') &&
    (canEditAnyBusiness(userData) ||
      canAnalystEditLinkedBusiness(userData, selectedOrganization));

  return (
    <BreadCrumbWrapper labels={labels}>
      <Card>
        <Card.Body>
          <OrganizationDetails
            secondaryHeaderButton={canEditBusiness
              ? {
                label: 'Edit',
                onClick: () => {
                  history.push(`/edo/business/${organizationId}/edit`);
                },
                disabled: false,
              }
              : null}
          />
        </Card.Body>
      </Card>
    </BreadCrumbWrapper>
  );
};

export default EdoBusinessDetail;
